import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { formatDateTime } from '../utils/dateUtils';

const RideTipPage = () => {
  const { rideId, bookingId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isDayBooking = location.pathname.includes('day-bookings');
  const entityId = isDayBooking ? bookingId : rideId;

  const [ride, setRide] = useState(null);
  const [tipAmount, setTipAmount] = useState(null);
  const [customTip, setCustomTip] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDetails();
  }, [entityId]);

  const fetchDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = isDayBooking
        ? `${process.env.REACT_APP_API_URL}/api/day-bookings/bookings/${entityId}`
        : `${process.env.REACT_APP_API_URL}/api/rides/${entityId}`;

      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setRide(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load details');
    } finally {
      setLoading(false);
    }
  };

  const handleTipSelection = (percentage) => {
    const baseFare = getBaseFare();
    const amount = baseFare * percentage;
    setTipAmount(amount);
    setCustomTip('');
  };

  const handleCustomTipChange = (value) => {
    setCustomTip(value);
    const amount = parseFloat(value);
    if (!isNaN(amount) && amount >= 0) {
      setTipAmount(amount);
    } else {
      setTipAmount(null);
    }
  };

  const handleNoTip = () => {
    setTipAmount(0);
    setCustomTip('');
  };

  const handleSubmit = async () => {
    if (tipAmount === null || tipAmount === undefined) {
      setError('Please select a tip amount or choose "No Tip"');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');

      const endpoint = isDayBooking
        ? `${process.env.REACT_APP_API_URL}/api/day-bookings/bookings/${entityId}/tip`
        : `${process.env.REACT_APP_API_URL}/api/rides/${entityId}/tip`;

      await axios.post(
        endpoint,
        { tip_amount: tipAmount },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      navigate('/rider/dashboard', {
        state: {
          message: tipAmount > 0
            ? `Tip of $${tipAmount.toFixed(2)} added successfully! Thank you!`
            : 'Thank you for your feedback!'
        }
      });
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to add tip');
    } finally {
      setSubmitting(false);
    }
  };

  const getBaseFare = () => {
    if (!ride) return 0;
    if (isDayBooking) {
      return parseFloat(ride.flat_rate || ride.total_fare || 0);
    }
    return parseFloat(ride.total_fare || ride.subtotal || 0);
  };

  const getRiderTotal = () => {
    if (!ride) return 0;
    if (isDayBooking) {
      const fare = parseFloat(ride.flat_rate || ride.total_fare || 0);
      const fee = parseFloat(ride.fare_processing_fee || 0);
      return fare + fee;
    }
    return ride.rider_total
      ? parseFloat(ride.rider_total)
      : parseFloat(ride.total_fare || 0) + parseFloat(ride.processing_fee || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (error && !ride) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-xl text-red-600 mb-4">{error}</div>
          <button
            onClick={() => navigate('/rider/dashboard')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const baseFare = getBaseFare();
  const riderTotal = getRiderTotal();
  const processingFee = riderTotal - baseFare;

  // Calculate tip processing fee using pass-through formula: tip / 0.971 - tip
  // This ensures driver receives exactly the tip amount after Stripe's cut
  const calculateTipFee = (tip) => tip > 0 ? Math.ceil((tip / 0.971 - tip) * 100) / 100 : 0;
  const tipProcessingFee = calculateTipFee(tipAmount || 0);
  const totalWithTip = riderTotal + (tipAmount || 0) + tipProcessingFee;

  const driverName = isDayBooking ? ride.driver_name : ride.driver_name;
  const pickupAddress = isDayBooking ? (ride.legs?.[0]?.pickup_address || 'N/A') : ride.pickup_address;
  const dropoffAddress = isDayBooking ? (ride.legs?.[ride.legs?.length - 1]?.dropoff_address || 'N/A') : ride.dropoff_address;
  const pickupTime = isDayBooking ? ride.start_time : ride.pickup_time;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Add a Tip</h1>
              <p className="text-gray-600">for {driverName || 'your driver'}</p>
            </div>
          </div>

          {/* Ride/Booking Details */}
          <div className="border-t border-gray-200 pt-4 space-y-3">
            {isDayBooking && (
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Booking Type</span>
                <span className="font-medium">
                  {ride.rate_type === 'half_day' ? 'Half-Day' : 'Full-Day'} Booking
                </span>
              </div>
            )}
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Date & Time</span>
              <span className="font-medium">
                {formatDateTime(pickupTime, 'full')}
              </span>
            </div>
            {!isDayBooking && (
              <div className="flex justify-between items-start">
                <span className="text-gray-600">Route</span>
                <div className="text-right max-w-[60%]">
                  <p className="font-medium text-sm">{pickupAddress}</p>
                  <p className="text-xs text-gray-500">to</p>
                  <p className="font-medium text-sm">{dropoffAddress}</p>
                </div>
              </div>
            )}

            {/* Fare Breakdown */}
            <div className="pt-3 border-t space-y-2">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">{isDayBooking ? 'Flat Rate' : 'Base Fare'}</span>
                <span>${baseFare.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Stripe Processing Fee</span>
                <span>${processingFee.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="text-gray-600 font-medium">Ride Total</span>
                <span className="text-lg font-bold text-gray-900">${riderTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tip Section */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Tip Your Driver</h2>
          <p className="text-gray-600 mb-6">100% of your tip goes to the driver</p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}

          {/* Preset Tip Buttons */}
          <div className="grid grid-cols-4 gap-3 mb-4">
            <button
              onClick={() => handleTipSelection(0.15)}
              className={`py-3 px-2 rounded-lg border-2 transition-all ${
                tipAmount === baseFare * 0.15
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <div className="font-bold">15%</div>
              <div className="text-sm text-gray-600">${(baseFare * 0.15).toFixed(2)}</div>
            </button>
            <button
              onClick={() => handleTipSelection(0.20)}
              className={`py-3 px-2 rounded-lg border-2 transition-all ${
                tipAmount === baseFare * 0.20
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <div className="font-bold">20%</div>
              <div className="text-sm text-gray-600">${(baseFare * 0.20).toFixed(2)}</div>
            </button>
            <button
              onClick={() => handleTipSelection(0.25)}
              className={`py-3 px-2 rounded-lg border-2 transition-all ${
                tipAmount === baseFare * 0.25
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <div className="font-bold">25%</div>
              <div className="text-sm text-gray-600">${(baseFare * 0.25).toFixed(2)}</div>
            </button>
            <button
              onClick={handleNoTip}
              className={`py-3 px-2 rounded-lg border-2 transition-all ${
                tipAmount === 0
                  ? 'border-gray-500 bg-gray-50 text-gray-700'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <div className="font-bold">No Tip</div>
              <div className="text-sm text-gray-600">$0.00</div>
            </button>
          </div>

          {/* Custom Tip Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Or enter a custom amount
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={customTip}
                onChange={(e) => handleCustomTipChange(e.target.value)}
                placeholder="0.00"
                className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Total Summary */}
          {tipAmount !== null && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Ride Total</span>
                  <span>${riderTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Tip</span>
                  <span>${(tipAmount || 0).toFixed(2)}</span>
                </div>
                {tipAmount > 0 && (
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Stripe Tip Fee</span>
                    <span>${tipProcessingFee.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t font-bold">
                  <span>New Total</span>
                  <span className="text-green-600">${totalWithTip.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={submitting || tipAmount === null}
            className={`w-full py-4 rounded-lg font-bold text-lg transition-all ${
              submitting || tipAmount === null
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {submitting ? 'Processing...' : tipAmount > 0 ? `Add $${tipAmount.toFixed(2)} Tip` : 'Continue Without Tip'}
          </button>

          {/* Back Button */}
          <button
            onClick={() => navigate('/rider/dashboard')}
            className="w-full mt-3 py-3 text-gray-600 hover:text-gray-800 font-medium"
          >
            Back to Dashboard
          </button>
        </div>

        {/* Info Note */}
        <div className="text-center text-sm text-gray-500">
          <p>Tips can be added within 24 hours of ride completion.</p>
          <p>100% of your tip goes directly to your driver.</p>
        </div>
      </div>
    </div>
  );
};

export default RideTipPage;
